import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '@/components/Layout';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { ArrowLeft, Edit, Trash2, Trophy, Dumbbell, MapPin, Calendar as CalendarIcon, User } from 'lucide-react';

const BACKEND_URL = (window.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || "");
const API = `${BACKEND_URL}/api`;

const formatDate = (iso) => {
  if (!iso) return '-';
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString('es-UY', { day: '2-digit', month: 'long', year: 'numeric' });
};

const SeguimientoDetail = () => {
  const { id } = useParams();
  usePageTitle('Detalle seguimiento');
  const navigate = useNavigate();
  const { user, isSuperAdmin, isAdmin } = useAuth();
  const [record, setRecord] = useState(null);
  const [playersMap, setPlayersMap] = useState({});
  const [categoryName, setCategoryName] = useState('');
  const [loading, setLoading] = useState(true);
  const [showDelete, setShowDelete] = useState(false);

  useEffect(() => {
    // Cargar el record primero (rápido) para mostrar header + Edit/Delete cuanto antes
    (async () => {
      try {
        const rec = await axios.get(`${API}/tracking/${id}`);
        setRecord(rec.data);
        setLoading(false);
        // Cargar detalles de jugadores y categorías en paralelo, sin bloquear UI
        const [plrs, cats] = await Promise.all([
          axios.get(`${API}/players?limit=500&scope=all`),
          axios.get(`${API}/categories`),
        ]);
        const map = {};
        (plrs.data?.data || []).forEach((p) => { map[p.id] = p; });
        setPlayersMap(map);
        const cat = (cats.data || []).find((c) => c.id === rec.data.category_id);
        setCategoryName(cat?.name || '');
      } catch (e) {
        console.error(e);
        toast.error('No se pudo cargar el seguimiento');
        navigate('/seguimiento');
      }
    })();
  }, [id, navigate]);

  const canEdit = record && (isSuperAdmin || isAdmin || record.scout_id === user?.id);

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/tracking/${id}`);
      toast.success('Seguimiento eliminado');
      navigate('/seguimiento');
    } catch (e) {
      toast.error('Error al eliminar');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-600"></div>
        </div>
      </Layout>
    );
  }

  if (!record) return null;

  const isPartido = record.event_type === 'partido';
  const EventIcon = isPartido ? Trophy : Dumbbell;

  return (
    <Layout>
      <div className="max-w-3xl mx-auto space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <Button variant="ghost" size="icon" onClick={() => navigate('/seguimiento')} data-testid="btn-back">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 truncate">
              {isPartido ? 'Partido' : 'Práctica'}
            </h1>
          </div>
          {canEdit && (
            <div className="flex gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/seguimiento/${id}/editar`)}
                data-testid="btn-edit"
              >
                <Edit className="w-4 h-4 sm:mr-1" />
                <span className="hidden sm:inline">Editar</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-red-600 hover:bg-red-50"
                onClick={() => setShowDelete(true)}
                data-testid="btn-delete"
              >
                <Trash2 className="w-4 h-4 sm:mr-1" />
                <span className="hidden sm:inline">Eliminar</span>
              </Button>
            </div>
          )}
        </div>

        {/* Info del evento */}
        <Card className="p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className={`p-2.5 rounded-lg ${isPartido ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
              <EventIcon className="w-5 h-5" />
            </div>
            <div>
              <h2 className="font-bold text-lg text-gray-900">
                {record.club || 'Club sin especificar'}
                {record.opponent && <span className="text-gray-500 font-normal"> vs {record.opponent}</span>}
              </h2>
              <div className="flex items-center gap-2 text-sm text-gray-500 mt-0.5">
                <CalendarIcon className="w-4 h-4" />
                {formatDate(record.date)}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
            {record.location && (
              <div className="flex items-center gap-2 text-gray-700">
                <MapPin className="w-4 h-4 text-gray-400" />
                {record.location}
              </div>
            )}
            {categoryName && (
              <div className="text-gray-700">
                <span className="text-gray-500">Categoría:</span> {categoryName}
              </div>
            )}
            {record.scout_name && (
              <div className="flex items-center gap-2 text-gray-700">
                <User className="w-4 h-4 text-gray-400" />
                Scout: {record.scout_name}
              </div>
            )}
          </div>
          {record.general_notes && (
            <div className="mt-3 pt-3 border-t text-sm text-gray-700 whitespace-pre-wrap">
              {record.general_notes}
            </div>
          )}
        </Card>

        {/* Jugadores observados */}
        <Card className="p-4">
          <h3 className="font-semibold mb-3">
            Jugadores observados ({(record.players_observed || []).length})
          </h3>
          {(record.players_observed || []).length === 0 ? (
            <p className="text-sm text-gray-500">No se registraron jugadores.</p>
          ) : (
            <div className="space-y-3">
              {record.players_observed.map((pe) => {
                const p = playersMap[pe.player_id];
                return (
                  <div
                    key={pe.player_id}
                    className="border rounded-lg p-3 hover:bg-gray-50 transition-colors"
                    data-testid={`observed-${pe.player_id}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <button
                        type="button"
                        onClick={() => navigate(`/players/${pe.player_id}`)}
                        className="font-medium text-gray-900 hover:text-brand-600 truncate text-left"
                      >
                        {p ? `${p.first_name} ${p.last_name}` : 'Jugador no disponible'}
                      </button>
                      {pe.rating != null && (
                        <Badge className="bg-brand-100 text-brand-700">⭐ {pe.rating}/10</Badge>
                      )}
                    </div>
                    {p && (
                      <p className="text-xs text-gray-500 mt-0.5">
                        {p.position || 'Sin posición'}{p.origin_club ? ` · ${p.origin_club}` : ''}
                      </p>
                    )}
                    {pe.comments && (
                      <p className="text-sm text-gray-700 mt-2 whitespace-pre-wrap">{pe.comments}</p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      {/* Delete confirmation */}
      <AlertDialog open={showDelete} onOpenChange={setShowDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar seguimiento?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se borrará el registro completo.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="btn-confirm-delete">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
};

export default SeguimientoDetail;
